import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Reseller context store
 * ─────────────────────────────────────────────────────────────────────
 * Super-admins operate across many resellers. Before they drill into a
 * specific customer's scope, they first "enter" a reseller from the
 * /resellers list. That reseller becomes the lens through which the
 * customers list is filtered and is surfaced in a sticky banner so it's
 * obvious which reseller's world they are currently working inside.
 *
 * Flow:
 *   1. super_admin visits /resellers, clicks "Enter" on a row
 *      → enter({id, name}) is called, and they are navigated to /customers
 *   2. /customers now filters by reseller_id
 *   3. super_admin picks a customer → customer-context store is set
 *   4. Both banners (reseller + customer) are visible
 *
 * Exiting the customer does NOT clear the reseller context — the user is
 * stepping back one level to the customer list. Only exiting the reseller
 * clears everything.
 *
 * Behaviour:
 *  - `enter(reseller)` stores the current reseller. All subsequent
 *    customer lookups scope to this reseller_id.
 *  - `exit()` clears it. The user returns to the reseller list.
 *  - The value is persisted to localStorage so F5 doesn't reset the flow.
 *
 * For reseller_owner and downstream roles this store is essentially
 * unused — they have an implicit reseller scope (their own) from the
 * auth-store tenant already. Only super_admin needs the drill-down.
 */

export interface CurrentReseller {
  id: string;
  name: string;
  /** Four-digit `reseller_number` — used to build scoped URLs like
   *  `/reseller/{N}/customer/{N}/...`. Older persisted contexts may
   *  not have this field; treat as null when missing. */
  resellerNumber?: number | null;
}

interface ResellerContextState {
  current: CurrentReseller | null;
  enter: (reseller: CurrentReseller) => void;
  exit: () => void;
}

export const useResellerContext = create<ResellerContextState>()(
  persist(
    (set) => ({
      current: null,
      enter: (reseller) => set({ current: reseller }),
      exit: () => set({ current: null }),
    }),
    {
      name: 'ivrello:reseller-context',
      storage: createJSONStorage(() => localStorage),
      // Only persist the current reseller — actions are restored on mount.
      partialize: (s) => ({ current: s.current }),
      version: 1,
    },
  ),
);

/** Convenience selectors so call sites don't have to spell them out. */
export const selectCurrentResellerId = (s: ResellerContextState) =>
  s.current?.id ?? null;

export const selectCurrentResellerName = (s: ResellerContextState) =>
  s.current?.name ?? null;
